import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ActivityLogPage from './pages/ActivityLogPage';
import ConsoleDashboardPage from './pages/ConsoleDashboardPage';
import OrdersListPage from './pages/orders/OrdersListPage';
import OrderDetailPage from './pages/orders/OrderDetailPage';
import OrderCreatePage from './pages/orders/OrderCreatePage';
import ProductsListPage from './pages/products/ProductsListPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import ProductCreatePage from './pages/products/ProductCreatePage';
import OrderLogicRulesPage from './pages/rules/OrderLogicRulesPage';
import OrderLogicRuleEditPage from './pages/rules/OrderLogicRuleEditPage';
import ProductRulesPage from './pages/rules/ProductRulesPage';
import RuleSimulatorPage from './pages/rules/RuleSimulatorPage';
import LendersListPage from './pages/lenders/LendersListPage';
import LenderDetailPage from './pages/lenders/LenderDetailPage';
import LenderWizardPage from './pages/lenders/wizard/LenderWizardPage';
import LenderReportsPage from './pages/lenders/LenderReportsPage';
import UsersListPage from './pages/users/UsersListPage';
import UserDetailPage from './pages/users/UserDetailPage';
import UserCreatePage from './pages/users/UserCreatePage';
import { AuthTokenProvider } from './hooks/useAuth';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AuthTokenProvider>
              <AppShell />
            </AuthTokenProvider>
          </ProtectedRoute>
        }
      >
        {/* The console lands on its dashboard: the key metrics across every lender and the recent admin activity. */}
        <Route index element={<ConsoleDashboardPage />} />
        {/* The orders dashboard, which used to be the landing page, keeps a route of its own. */}
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="activity-log" element={<ActivityLogPage />} />
        <Route path="orders" element={<OrdersListPage />} />
        <Route path="orders/new" element={<OrderCreatePage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route path="products" element={<ProductsListPage />} />
        <Route path="products/new" element={<ProductCreatePage />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route path="rules/product" element={<ProductRulesPage />} />
        <Route path="rules/order-logic" element={<OrderLogicRulesPage />} />
        <Route path="rules/order-logic/new" element={<OrderLogicRuleEditPage />} />
        <Route path="rules/order-logic/:urn" element={<OrderLogicRuleEditPage />} />
        <Route path="rules/simulator" element={<RuleSimulatorPage />} />
        <Route path="lenders" element={<LendersListPage />} />
        <Route path="lenders/new" element={<LenderWizardPage />} />
        <Route path="lenders/:urn" element={<LenderDetailPage />} />
        <Route path="lenders/:urn/reports" element={<LenderReportsPage />} />
        <Route path="users" element={<UsersListPage />} />
        <Route path="users/new" element={<UserCreatePage />} />
        <Route path="users/:urn" element={<UserDetailPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
